import buildQuery, { type QueryOptions } from 'odata-query';

interface OdataQueryParams {
  $filter?: string;
  $search?: string;
  $top?: number;
  $skip?: number;
  $orderby?: string;
  $select?: string;
  $expand?: string;
  $count?: boolean;
}

export type OdataQuery<T> = Partial<QueryOptions<T>>;

/**
 * Escapes special characters for OData search string literals.
 * OData parsers (especially .NET) use JSON-style escaping rules:
 * - Backslash (\) must be escaped as \\
 * - Double quote (") must be escaped as \"
 */
export function escapeOdataSearchString(value: string): string {
  return value
    .replace(/\\/g, '\\\\') // Escape backslashes first
    .replace(/"/g, '\\"'); // Then escape quotes
}

/**
 * The tests taught me how the function actually behaves:
 * - My assumption: Values would be URL-encoded in the output BUT
 * - URLSearchParams decodes encoded values back to normal strings for OData
 * - The encoding/decoding round-trip is intentional to handle special characters and spaces
 */
export function buildOdataQuery<T>(query: Partial<QueryOptions<T>>) {
  query.search = query.search
    ? `"${encodeURIComponent(escapeOdataSearchString(query.search))}"`
    : undefined;

  return Object.fromEntries(new URLSearchParams(buildQuery(query)).entries()) as OdataQueryParams;
}
